import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CancelButton from '~/components/core/Buttons/CancelButton';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import TextFieldFormik from '~/components/TextFieldFormik';
import { isLocaleRegionIsUs } from '~/Utils/regionUtils';

export type VitesseHub = 'standard' | 'us';

export interface VitesseConfiguration {
  client_id: string;
  client_secret: string;
  hub: VitesseHub;
}

interface VitesseSetupDialogProps {
  onSubmit: (configuration: VitesseConfiguration) => Promise<void> | void;
  onClose: () => void;
  configuration?: VitesseConfiguration;
}

const VitesseSetupDialog: React.FC<VitesseSetupDialogProps> = ({ onSubmit, onClose, configuration }) => {
  const { classes } = useStyles();

  return (
    <Formik
      initialValues={{
        client_id: configuration?.client_id || '',
        client_secret: configuration?.client_secret || '',
        // Default a new config to the deployment region's hub (US locale -> US hub); an existing
        // config keeps its stored value. The hub only takes effect in production.
        hub: configuration?.hub || (isLocaleRegionIsUs() ? 'us' : 'standard'),
      }}
      validationSchema={Yup.object().shape({
        client_id: Yup.string().required('Required'),
        client_secret: Yup.string().required('Required'),
        hub: Yup.string().oneOf(['standard', 'us']).required('Required'),
      })}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await onSubmit(values);
        } catch (error) {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, handleSubmit, values, setFieldValue }) => (
        <CardDialog
          title="Vitesse Integration Details"
          isDialog
          preventClose={isSubmitting}
          maxWidth="sm"
          fullWidth
          onClose={onClose}
        >
          <div className="grid gap-20">
            <TextFieldFormik id="client_id" label="Client ID" className={classes.textField} fullWidth />
          </div>
          <div className="grid gap-20">
            <TextFieldFormik id="client_secret" label="Client Secret" className={classes.textField} fullWidth />
          </div>
          <div className="grid gap-20">
            <SingleSelectField
              label="Hub"
              value={values.hub}
              onChange={(value) => setFieldValue('hub', (value as VitesseHub) ?? 'standard')}
              className={classes.textField}
              fullWidth
            >
              <MenuItem value="standard">Standard (UK/EU)</MenuItem>
              <MenuItem value="us">US</MenuItem>
            </SingleSelectField>
          </div>
          <div className="mt-px flex w-full justify-end">
            <CancelButton disabled={isSubmitting} onClick={onClose} />
            <Button variant="contained" color="primary" disabled={isSubmitting} onClick={() => handleSubmit()}>
              Save
            </Button>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default VitesseSetupDialog;
